import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { customerService } from '../../../services/customerService';
import { useBottomSheet } from '../../../hooks/useBottomSheet';

const AddCustomerModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    aduType: 'detached',
    leadSource: 'website',
    estimatedBudget: '',
    projectPreferences: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDemoError, setIsDemoError] = useState(false);

  const {
    sheetStyle,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    scrollRef,
    showTopShadow,
    showBottomShadow,
  } = useBottomSheet(onClose);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setIsDemoError(false);
    setLoading(true);
    try {
      await customerService?.create({
        ...formData,
        estimatedBudget: formData?.estimatedBudget ? parseFloat(formData?.estimatedBudget) : null,
        lastContact: new Date()?.toISOString()
      });
      onSuccess?.();
      onClose();
      setFormData({
        name: '',
        email: '',
        phone: '',
        propertyAddress: '',
        aduType: 'detached',
        leadSource: 'website',
        estimatedBudget: '',
        projectPreferences: ''
      });
    } catch (err) {
      if (err?.message?.toLowerCase()?.includes('demo mode')) {
        setIsDemoError(true);
      } else {
        setError(err?.message || 'Failed to create customer');
      }
    } finally {
      setLoading(false);
    }
  };

  const aduTypeOptions = [
    { value: 'detached', label: 'Detached' },
    { value: 'attached', label: 'Attached' },
    { value: 'garage_conversion', label: 'Garage Conversion' },
    { value: 'basement_conversion', label: 'Basement Conversion' },
    { value: 'junior_adu', label: 'Junior ADU' },
  ];

  const leadSourceOptions = [
    { value: 'website', label: 'Website' },
    { value: 'referral', label: 'Referral' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'trade_show', label: 'Trade Show' },
    { value: 'other', label: 'Other' },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[200] p-0 sm:p-4">
      <div
        className="bg-card w-full sm:rounded-xl sm:max-w-2xl shadow-2xl rounded-t-2xl max-h-[92vh] flex flex-col"
        style={{
          paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))',
          ...(typeof window !== 'undefined' && window.innerWidth < 640 ? sheetStyle : {})
        }}
      >
        {/* Enhanced drag handle on mobile */}
        <div
          className="sm:hidden flex-shrink-0 flex flex-col items-center pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full" />
          <span className="text-[10px] text-muted-foreground/40 mt-1 font-medium tracking-wide">DRAG TO CLOSE</span>
        </div>

        {/* Sticky header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0 bg-card">
          <h2 className="text-lg font-semibold text-foreground">Add New Customer</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            disabled={loading}
          >
            <Icon name="X" size={22} />
          </button>
        </div>

        {/* Scroll indicator - top */}
        <div
          className={`flex-shrink-0 h-5 pointer-events-none transition-opacity duration-200 ${showTopShadow ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: 'linear-gradient(to bottom, hsl(var(--card)) 0%, transparent 100%)', marginBottom: '-20px', position: 'relative', zIndex: 2 }}
        />

        {/* Scrollable form */}
        <form ref={scrollRef} onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {isDemoError && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <Icon name="Lock" size={16} className="text-amber-600 flex-shrink-0" />
              <span className="text-sm font-medium">This action is not available in demo mode. <a href="/sign-up" className="underline font-semibold hover:text-amber-900">Sign up for a full account</a> to add customers.</span>
            </div>
          )}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Name <span className="text-destructive">*</span>
              </label>
              <Input type="text" name="name" value={formData?.name} onChange={handleChange} required placeholder="Enter customer name" />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Email <span className="text-destructive">*</span>
              </label>
              <Input type="email" name="email" value={formData?.email} onChange={handleChange} required placeholder="customer@email.com" />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Phone</label>
              <Input type="tel" name="phone" value={formData?.phone} onChange={handleChange} placeholder="(555) 123-4567" />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Estimated Budget</label>
              <Input type="number" name="estimatedBudget" value={formData?.estimatedBudget} onChange={handleChange} placeholder="150000" min="0" step="1000" />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">ADU Type</label>
              <Select
                name="aduType"
                value={formData?.aduType}
                options={aduTypeOptions}
                onChange={(val) => setFormData(prev => ({ ...prev, aduType: val }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Lead Source</label>
              <Select
                name="leadSource"
                value={formData?.leadSource}
                options={leadSourceOptions}
                onChange={(val) => setFormData(prev => ({ ...prev, leadSource: val }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Property Address</label>
            <Input type="text" name="propertyAddress" value={formData?.propertyAddress} onChange={handleChange} placeholder="1234 Main St, City, State ZIP" />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Project Preferences</label>
            <textarea
              name="projectPreferences"
              value={formData?.projectPreferences}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm resize-none"
              placeholder="Describe customer preferences and requirements..."
            />
          </div>
        </form>

        {/* Scroll indicator - bottom */}
        <div
          className={`flex-shrink-0 h-5 pointer-events-none transition-opacity duration-200 ${showBottomShadow ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: 'linear-gradient(to top, hsl(var(--card)) 0%, transparent 100%)', marginTop: '-20px', position: 'relative', zIndex: 2 }}
        />

        {/* Sticky footer */}
        <div className="flex gap-3 px-5 pt-3 border-t border-border flex-shrink-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading} className="flex-1 h-12">
            Cancel
          </Button>
          <Button type="submit" disabled={loading} className="flex-1 h-12" onClick={handleSubmit}>
            {loading ? 'Creating...' : 'Create Customer'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddCustomerModal;